import React from 'react';
import { Chip, Stack, Text } from '@billda/design-system';
import { useFormContext } from 'react-hook-form';
import MissingLabourItemsCreator from './MissingLabourItemsCreator';
import PlotPreviewAccordion from './PlotPreviewAccordion';
import { formatCurrency } from '../../../../../../shared/utils/currency';
import { SiteImportPreviewResponse } from '../../../../api/sitesApi';
import { SiteImportFormData } from '../../../../schemas/siteImportSchema';

interface ReviewConfirmStepProps {
  previewData: SiteImportPreviewResponse | null;
  importPath: 'template' | 'csv' | null;
  onAllResolved: () => void;
}

const ReviewConfirmStep: React.FC<ReviewConfirmStepProps> = ({
  previewData,
  importPath,
  onAllResolved,
}) => {
  const { getValues } = useFormContext<SiteImportFormData>();
  const { name, slug, what3words } = getValues();

  if (!previewData) {
    return (
      <div
        className="rounded-xl border border-border-default bg-surface-default p-6"
        data-testid="confirm-import-step"
      >
        <Text variant="body1" className="text-content-error">
          No preview data available. Please complete the previous steps first.
        </Text>
      </div>
    );
  }

  const totalItems = previewData.plots.reduce(
    (sum, plot) => sum + plot.labourItems.length,
    0
  );
  const totalValue = previewData.plots.reduce(
    (sum, plot) =>
      sum + plot.labourItems.reduce((itemSum, item) => itemSum + item.value, 0),
    0
  );

  return (
    <Stack spacing={2} data-testid="confirm-import-step">
      {/* Summary card */}
      <div
        className="grid grid-cols-1 gap-3 rounded-2xl border border-border-default bg-surface-raised p-5 sm:grid-cols-2"
        data-testid="import-summary"
      >
        <div>
          <Text variant="caption" color="secondary">
            Site name
          </Text>
          <Text variant="body2" className="font-medium">
            {name}
          </Text>
        </div>
        <div>
          <Text variant="caption" color="secondary">
            Site code
          </Text>
          <Text variant="body2" className="font-mono font-medium">
            {slug}
          </Text>
        </div>
        <div>
          <Text variant="caption" color="secondary">
            Location
          </Text>
          <Text variant="body2" className="font-medium">
            {what3words || 'Not provided'}
          </Text>
        </div>
        <div>
          <Text variant="caption" color="secondary">
            Source
          </Text>
          <Text variant="body2" className="font-medium">
            {importPath === 'template' ? 'Trade template' : 'CSV upload'}
          </Text>
        </div>
      </div>

      {/* Missing labour items */}
      {previewData.missingLabourDefinitions &&
        previewData.missingLabourDefinitions.length > 0 && (
          <MissingLabourItemsCreator
            missingItems={previewData.missingLabourDefinitions}
            onAllResolved={onAllResolved}
          />
        )}

      {/* Stats row */}
      <Stack direction="row" spacing={1}>
        <Chip
          label={`${previewData.plots.length} plots`}
          size="sm"
          color="info"
          variant="outlined"
        />
        <Chip
          label={`${totalItems} labour items`}
          size="sm"
          color="info"
          variant="outlined"
        />
        <Chip
          label={`${formatCurrency(totalValue)} total`}
          size="sm"
          color="success"
          variant="outlined"
        />
      </Stack>

      {/* Preview table */}
      <PlotPreviewAccordion plots={previewData.plots} />
    </Stack>
  );
};

export default ReviewConfirmStep;
